import React, { useMemo, useState, useEffect } from 'react';
import { Field } from 'formik';
import CodeMirror from '@uiw/react-codemirror';
import { css } from '@codemirror/lang-css';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { linter } from '@codemirror/lint';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const validateCSS = cssString => {
  if (!cssString || cssString.trim() === '') {
    return { isValid: true, errors: [] };
  }

  const errors = [];
  const lines = cssString.split('\n');

  let braceCount = 0;
  let braceStack = [];

  lines.forEach((line, index) => {
    const lineNumber = index + 1;
    const trimmedLine = line.trim();

    for (let i = 0; i < trimmedLine.length; i++) {
      const char = trimmedLine[i];

      if (char === '{') {
        braceCount++;
        braceStack.push({ line: lineNumber, type: '{' });
      } else if (char === '}') {
        braceCount--;
        if (braceCount < 0) {
          errors.push({
            line: lineNumber,
            message: 'Unexpected closing brace',
          });
        } else {
          braceStack.pop();
        }
      }
    }
  });

  if (braceCount > 0) {
    braceStack.forEach(brace => {
      errors.push({
        line: brace.line,
        message: 'Unclosed opening brace',
      });
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const cssLinter = linter(view => {
  const diagnostics = [];
  const content = view.state.doc.toString();
  const validation = validateCSS(content);

  if (!validation.isValid) {
    const lines = content.split('\n');
    validation.errors.forEach(error => {
      let pos = 0;
      for (let i = 0; i < error.line - 1; i++) {
        pos += lines[i].length + 1;
      }

      diagnostics.push({
        from: pos,
        to: pos + (lines[error.line - 1]?.length || 0),
        severity: 'error',
        message: error.message,
      });
    });
  }

  return diagnostics;
});

const CSSEditor = ({ id, name, className, placeholder, ...props }) => {
  const [validationResult, setValidationResult] = useState({ isValid: true, errors: [] });

  const extensions = useMemo(() => [css(), cssLinter], []);

  return (
    <Field name={name}>
      {({ field, form }) => {
        const handleChange = value => {
          form.setFieldValue(name, value);

          const result = validateCSS(value);
          setValidationResult(result);
        };

        useEffect(() => {
          if (field.value) {
            const result = validateCSS(field.value);
            setValidationResult(result);
          }
        }, []);

        return (
          <div className={className}>
            <div className="border border-gray-50 dark:border-border shadow-sm rounded overflow-hidden">
              <CodeMirror
                id={id}
                value={field.value || ''}
                onChange={handleChange}
                extensions={extensions}
                theme={vscodeDark}
                placeholder={placeholder}
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  highlightActiveLine: true,
                  foldGutter: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                  highlightSelectionMatches: true,
                }}
                minHeight="240px"
                maxHeight="600px"
                {...props}
              />
            </div>

            {!validationResult.isValid && (
              <div className="mt-2 p-3 bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-md">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">
                      CSS syntax errors
                    </h4>
                    <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                      {validationResult.errors.map((error, index) => (
                        <li key={index} className="font-mono">
                          Line {error.line}: {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </Field>
  );
};

export default CSSEditor;
